export const theme = {
  colors: {
    background: "#ffffff", // Clean white background
    neutral: {
      100: "#ffffff", // Pure white
      200: "#f9fafb", // Slightly off-white
      300: "#f3f4f6", // Light gray
      400: "#e5e7eb", // Muted gray
      500: "#d1d5db", // Soft gray
      600: "#9ca3af", // Medium gray
      700: "#6b7280", // Darker gray for contrast
      800: "#4b5563", // Heading contrast
      900: "#1f2937", // Deep gray for dark mode elements
    },

    primary: {
      light: "#EEF2FF", // Softer light blue for better eye comfort
      main: "#4F46E5", // Indigo - better for education/tech feel
      dark: "#3730A3", // Darker indigo for depth
    },

    secondary: {
      main: "#FCD34D", // Warm gold for accents
      dark: "#FBBF24", // Darker shade for hover
    },

    accent: {
      main: "#22C55E", // Fresh green - symbolizes growth/learning
      dark: "#16A34A",
    },

    text: {
      primary: "#1F2937", // Keeping existing - good for readability
      secondary: "#4B5563", // Slightly darker for better contrast
      tertiary: "#6B7280", // Keeping existing
      inverted: "#ffffff",
      highlight: "#4F46E5", // For emphasizing important text
    },

    success: "#10B981",
    error: "#EF4444",

    disabled: {
      background: "#E5E7EB",
      text: "#9CA3AF",
    },

    gradients: {
      primary: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
      secondary: "linear-gradient(45deg, #3B82F6 0%, #2563EB 100%)",
      accent: "linear-gradient(45deg, #FCD34D 0%, #F59E0B 100%)",
    },

    // Add semantic colors for learning context
    semantic: {
      success: "#22C55E", // Positive feedback
      error: "#DC2626",   // Incorrect answers
      warning: "#F59E0B", // Alerts/notifications
      info: "#3B82F6",    // Tips/hints
      progress: "#8B5CF6" // Learning progress
    },
  },

  globalBackground: `
    background-color: #f9fafb; /* Lighter background */
    background-image: radial-gradient(circle, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
    background-size: 6px 6px; /* Finer grid for a professional look */
    
    &::after {
      background: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.02) 0%, transparent 70%);
    }
  `,

  typography: {
    fonts: {
      primary: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      secondary: "'Inter', sans-serif",
      mono: "'JetBrains Mono', monospace", // Great for code examples
    },
    sizes: {
      xs: "0.75rem", // 12px
      sm: "0.875rem", // 14px
      md: "1rem", // 16px
      lg: "1.25rem", // 20px
      xl: "1.5rem", // 24px
      "2xl": "2rem", // 32px
      "3xl": "2.5rem", // 40px
      "4xl": "3rem", // 48px
    },
    weights: {
      light: "300",
      normal: "400",
      medium: "500",
      semibold: "600",
      bold: "700",
      extrabold: "800",
    },
    lineHeights: {
      tight: "1.2",
      normal: "1.5",
      relaxed: "1.7",
    },
    letterSpacing: {
      tight: "-0.02em",
      normal: "0",
      wide: "0.08em",
    },
    variants: {
      h1: {
        fontSize: "3rem",
        fontWeight: "700",
        lineHeight: "1.2",
        letterSpacing: "-0.02em",
      },
      h2: {
        fontSize: "2.5rem",
        fontWeight: "600",
        lineHeight: "1.3",
      },
      body: {
        fontSize: "1.125rem", // Slightly larger for better readability
        fontWeight: "400",
        lineHeight: "1.6", // Increased for better readability
      },
      small: {
        fontSize: "0.875rem",
        fontWeight: "400",
        lineHeight: "1.5",
      },
      caption: { // New variant for explanatory text
        fontSize: "0.75rem",
        fontWeight: "500",
        letterSpacing: "0.02em",
      },
      button: {
        fontSize: "1.125rem",
        fontWeight: "600",
      },
    },
  },

  spacing: {
    xs: "4px",
    sm: "8px",
    md: "16px",
    lg: "24px",
    xl: "32px",
    "2xl": "40px",
    "3xl": "56px",
  },

  breakpoints: {
    sm: "480px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },

  buttons: {
    primary: {
      background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
      color: "#FFFFFF",
      hover: {
        background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
      },
    },
    secondary: {
      background: "transparent",
      color: "#374151",
      border: "2px solid #E5E7EB",
      borderRadius: "12px",
      padding: "12px 24px",
      hover: {
        background: "#F9FAFB",
        borderColor: "#374151",
      },
    },
    accent: {
      background: "linear-gradient(45deg, #FCD34D 0%, #FBBF24 100%)",
      color: "#FFFFFF",
      borderRadius: "12px",
      padding: "14px 28px",
      hover: {
        transform: "translateY(-1px)",
        shadow: "0 4px 6px -1px rgba(0, 0, 0, 0.2)",
      },
    },
  },

  layout: {
    maxWidth: "1440px",
    sectionPadding: {
      x: "24px",  // Horizontal padding
      y: "96px",  // Vertical padding
      mobile: "48px 16px", // Responsive padding for mobile
    },
    containerPadding: {
      x: "24px",  // Give containers some breathing room
      y: "0",
    },
    fullWidth: "100%",  // Changed from 100vw to avoid horizontal scrollbar issues
    contentWidth: "min(1440px, 100%)",  // Makes it responsive
    gridGap: "16px",
  },

  shadows: {
    sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    focus: "0 0 0 3px rgba(66, 153, 225, 0.5)",
  },

  transitions: {
    default: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    fast: "all 0.1s ease-in-out",
    spring: "all 0.3s cubic-bezier(.25, .1, .25, 1.4)",
  },

  radii: {
    sm: "4px",
    md: "8px",
    lg: "12px",
    xl: "24px",
    full: "9999px",
  },

  borderRadius: "12px",

  // Add new section for common learning components
  learning: {
    card: {
      background: "#ffffff",
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      shadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
      hoverShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
    },
    quiz: {
      background: "#F9FAFB",
      correct: "#ECFDF5",
      incorrect: "#FEF2F2",
      hover: "#F3F4F6",
    },
    progress: {
      background: "#E5E7EB",
      fill: "#4F46E5",
      borderRadius: "9999px",
    },
  },
};
